import { motion } from 'framer-motion';
import SafeImage from './SafeImage';
import { ABOUT_IMAGE, IMAGE_FALLBACK_CHAIN } from '../config/siteImages';
import { COMPANY_NAME } from '../config/siteInfo';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] },
});

export default function About({ section }) {
  const eyebrow = section?.eyebrow || `About ${COMPANY_NAME}`;
  const title = section?.title || 'About Us – Dragon Metalworks';
  const subtitle =
    section?.subtitle ||
    'Dragon Metalworks is a London-based steel fabrication company specialising in all aspects of structural steel and custom metalwork.';
  const paragraph =
    section?.paragraph ||
    'We design, fabricate, and install a wide range of steelwork, including structural steel, gates, railings, staircases, balconies, mezzanines, and bespoke metal solutions for residential and commercial projects.\n\nOur team handles everything from design and fabrication to site installation, ensuring every project is completed with precision, strength, and attention to detail.\n\nFrom large structural frameworks to detailed finishing work, we cover all types of steel fabrication with reliable quality and workmanship.\n\nStrong steel. Professional finish. Built to last.';

  const pillars = section?.pillars?.length
    ? section.pillars.map((p) => ({ title: p.title, desc: p.description || p.desc }))
    : [];

  const certs = section?.certTags?.length ? section.certTags : [];

  return (
    <section className="about" id="about">
      <div className="container">
        <div className="about-grid">
          <motion.div
            className="about-image-block"
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.75, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="about-img-main">
              {/*
                About column image: ABOUT_IMAGE in src/config/siteImages.js → public/images/
              */}
              <SafeImage
                src={section?.imageUrl || ABOUT_IMAGE}
                fallbackSrcs={IMAGE_FALLBACK_CHAIN}
                alt={section?.imageAlt || `${COMPANY_NAME} fabrication workshop`}
                wrapperClassName="about-img-wrap-inner"
                imgClassName="about-img-el about-img--yard"
              />
            </div>
            <div className="about-corner-accent" aria-hidden />
            <div className="about-img-badge">
              <div className="num">500+</div>
              <div className="desc">Projects delivered</div>
            </div>
          </motion.div>

          <div className="about-content">
            <motion.div className="eyebrow" {...fadeUp(0.08)}>
              {eyebrow}
            </motion.div>

            <motion.h2 className="section-title" {...fadeUp(0.14)}>
              {title.split('\n').map((line, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={i}>
                  {i > 0 ? <br /> : null}
                  {line}
                </span>
              ))}
            </motion.h2>

            {subtitle ? (
              <motion.p className="section-sub" {...fadeUp(0.22)}>
                {subtitle}
              </motion.p>
            ) : null}

            {paragraph ? (
              <motion.p className="about-p" {...fadeUp(0.28)}>
                {paragraph}
              </motion.p>
            ) : null}

            {pillars.length > 0 ? (
              <motion.div
                className="about-values"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                variants={{
                  hidden: {},
                  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
                }}
              >
                {pillars.map((v) => (
                  <motion.div
                    key={v.title}
                    className="value-card"
                    variants={{
                      hidden: { opacity: 0, y: 16 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
                    }}
                  >
                    <div className="value-card-title">{v.title}</div>
                    <div className="value-card-desc">{v.desc}</div>
                  </motion.div>
                ))}
              </motion.div>
            ) : null}

            {certs.length > 0 ? (
              <motion.div className="about-cert-bar" {...fadeUp(0.42)}>
                {certs.map((c) => (
                  <span key={c} className="cert-tag">
                    <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    {c}
                  </span>
                ))}
              </motion.div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
